import { gatewayApi } from '@/lib/hrApi';

// Tenant self-service scheduling. The gateway exposes the scheduler's tenant
// surface at /api/v1/tenant/scheduler/* (it pins our tenant_id + injects the
// internal secret). We only ever see/manage our own overrides of the curated
// catalog of automations. gatewayApi attaches the access token + handles refresh.
const BASE = '/api/v1/tenant/scheduler';

export type ScheduleType = 'cron' | 'once' | 'interval';
export type RunStatus = 'pending' | 'published' | 'failed' | 'skipped';

export interface ScheduledJob {
  id: string;
  tenant_id: string | null;
  job_key: string;
  subject: string;
  schedule_type: ScheduleType;
  cron_expression?: string;
  interval_seconds?: number;
  timezone: string;
  enabled: boolean;
  max_retries: number;
  retry_delay_seconds: number;
  last_run_at?: string | null;
  next_run_at?: string | null;
  created_at: string;
  updated_at: string;
}

/** One catalog automation: the platform default + this tenant's override (if any). */
export interface TenantJobView {
  job_key: string;
  label: string;
  description: string;
  subject: string;
  default: ScheduledJob | null;
  override: ScheduledJob | null;
}

export interface JobRun {
  id: string;
  status: RunStatus;
  attempt: number;
  reason?: string;
  error_message?: string;
  started_at: string;
  finished_at?: string | null;
}

export interface SchedulePut {
  schedule_type: ScheduleType;
  cron_expression?: string;
  interval_seconds?: number;
  timezone: string;
  enabled?: boolean;
}

export const listTenantJobs = async (): Promise<TenantJobView[]> => {
  const { data } = await gatewayApi.get(`${BASE}/jobs`);
  return data?.jobs ?? [];
};

export const putTenantJob = async (jobKey: string, body: SchedulePut): Promise<ScheduledJob> => {
  const { data } = await gatewayApi.put(`${BASE}/jobs/${jobKey}`, body);
  return data;
};

export const deleteTenantJob = async (jobKey: string): Promise<void> => {
  await gatewayApi.delete(`${BASE}/jobs/${jobKey}`);
};

export const runTenantJob = async (jobKey: string): Promise<JobRun> => {
  const { data } = await gatewayApi.post(`${BASE}/jobs/${jobKey}/run-now`, {});
  return data?.run;
};

export const listTenantJobRuns = async (jobKey: string): Promise<JobRun[]> => {
  const { data } = await gatewayApi.get(`${BASE}/jobs/${jobKey}/runs`);
  return data?.runs ?? [];
};
